import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { finalize, Observable, tap } from 'rxjs';

import { MonitoringService } from './monitoring.service';

@Injectable()
export class MonitoringInterceptor implements HttpInterceptor {
  #injector = inject(Injector);
  get #monitoringService() {
    return this.#injector.get(MonitoringService);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.shouldSkipMonitoring(req.url)) {
      return next.handle(req);
    }

    const operationId = this.generateOperationId();
    const method = req.method;
    const url = req.url;
    const urlPath = this.getUrlPath(url);

    try {
      this.#monitoringService.trackOperation('http.request', operationId, {
        'http.method': method,
        'http.url': url,
        'http.path': urlPath,
        'http.has_body': !!req.body,
      });
    } catch (error) {
      // Ensure monitoring failures don't block the HTTP call
      if (this.#monitoringService.isDebugEnabled()) {
        console.warn('[Monitoring] Failed to track operation:', error);
      }
    }

    const trackedReq = req.clone({
      headers: req.headers.set('X-Operation-ID', operationId),
    });

    const startTime = Date.now();

    return next.handle(trackedReq).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            const duration = Date.now() - startTime;
            try {
              this.#monitoringService.completeOperation('http.request', operationId, true, {
                'http.status_code': event.status,
                'http.duration_ms': duration,
                'http.response_size': this.getResponseSize(event),
              });
            } catch (error) {
              // Ensure monitoring failures don't block the HTTP call
              if (this.#monitoringService.isDebugEnabled()) {
                console.warn('[Monitoring] Failed to complete operation:', error);
              }
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          const duration = Date.now() - startTime;
          try {
            this.#monitoringService.completeOperation('http.request', operationId, false, {
              'http.status_code': error.status,
              'http.duration_ms': duration,
              'http.error': error.message,
              'http.error_type': error.name,
            });

            this.#monitoringService.trackError(error, 'http.request', {
              'operation.id': operationId,
              'http.method': method,
              'http.url': url,
              'http.path': urlPath,
            });
          } catch (monitoringError) {
            // Ensure monitoring failures don't block error handling
            if (this.#monitoringService.isDebugEnabled()) {
              console.warn('[Monitoring] Failed to track error:', monitoringError);
            }
          }
        },
      }),
      finalize(() => undefined),
    );
  }

  private shouldSkipMonitoring(url: string): boolean {
    const skipPatterns = ['/monitoring/', '/traces', '/metrics'];

    return skipPatterns.some((pattern) => url.includes(pattern));
  }

  private generateOperationId(): string {
    return `req-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  }

  private getUrlPath(url: string): string {
    try {
      const urlObj = new URL(url);

      return urlObj.pathname;
    } catch {
      // If URL parsing fails, just return the original URL
      return url;
    }
  }

  private getResponseSize(response: HttpResponse<unknown>): number {
    return response.body ? JSON.stringify(response.body).length : 0;
  }
}
